import type { Config, Player } from '@schibsted-svp/web-player';
import type { Asset } from '@schibsted-svp/svp-api-types';
import { ConsentManager } from '@vgtv/consent-manager';

import {
  getAssetAspectRatio,
  getAssetMetadata,
} from '../../utils/player/asset';
import { getCategoryMetadataValue } from '../../utils/player/category';
import { getDeviceType } from '../../utils/device';
import type {
  AdnSiteConfig,
  DeviceType,
  PlacementType,
  XandrKeywords,
} from '../../config/adn/types';
import type {
  AdnContext,
  PlayerConfigOptions,
  Plugin,
} from '../../types/global';
import { PpidWrapper } from '../../config/adn/ppid/PpidWrapper';
import { getScreenOrientation } from '../../utils/screenOrientation';
import { prepareXandrKeywords } from '../../config/adn/prepareKeywords';
import { getAdPermission } from '../../config/adn/getAdPermission';
import getUserId from '../../config/adn/getUserId';
import { getPlayerAspectRatio } from '../../config/adn/getPlayerAspectRatio';
import { buildConsentKeywords } from '../../config/adn/buildConsentKeywords';
import { getAssetProvider, getConfigProvider } from '../../utils/player/player';
import { configAb, configSvd } from '../../config/adn/ab/config';

import adsConfig from './config';
import { resolveIsLoggedIn } from './ab/loginState';
import { getVmapKeywords } from '../../config/adn/ab/getVmapKeywords';

import AdErrorParam = jwplayer.AdErrorParam;

type SvpVmapParams = {
  supplyType: string;
  sdkType?: 'ima';
  site: string;
  forceAds?: boolean;
  bumper?: boolean;
  externalUid?: string;
  tcf_consent?: boolean;
  gdpr_consent?: string;
  eids?: string;
  client?: string;
};

interface AdnPluginConfig {
  options: PlayerConfigOptions;
  context: AdnContext;
  providerCountry: 'no' | 'se';
}

const LONGFORM_DURATION = 2.5 * 60 * 1000; // 2.5 minutes in ms

function getPropertyFromAssetOrCategory(
  asset: Asset | undefined,
  propertyName: string
) {
  return (
    getAssetMetadata(asset)[propertyName] ??
    getCategoryMetadataValue(asset?.category, propertyName)
  );
}

export interface AdnPlugin extends Plugin<
  AdnPlugin,
  { options: AdnPluginConfig }
> {
  options: PlayerConfigOptions;
  context: AdnContext;
  providerCountry: 'no' | 'se';
  skipAd: boolean;
  asset?: Asset;
  player: Player;
  ppidWrapper: PpidWrapper;

  getPlacement(placementType: PlacementType | 'pausead'): string | undefined;
  hasPlacement(placementType: PlacementType | 'pausead'): boolean;
  handleAssetReady(): void;
  handleAdError(error: AdErrorParam): Promise<void>;
  getAsset(): Asset | undefined;
  getVmap: (params: {
    site: string;
    experimentMap: Record<string, string>;
    consentManager: ConsentManager;
  }) => Promise<any>;

  get muted(): boolean;
  get config(): Config;
  get siteConfig(): AdnSiteConfig<'vgtv'> | undefined;
  get deviceType(): DeviceType;
  get adsDisabled(): boolean;
  get prerollFormat(): 'preroll' | 'bumper' | 'sponsorroll';
  get pcFormat(): 'preroll' | 'bumper' | 'adfree' | 'skip';
  get prerollSubtype(): 'preroll' | 'bumper' | 'longform' | 'sponsorroll';
}

export const adnPlugin: AdnPlugin = {
  options: {} as any,
  player: undefined as any,
  context: {} as any,
  asset: undefined,
  skipAd: false,
  ppidWrapper: null!,
  providerCountry: 'no',

  getName() {
    return 'AdnPlugin';
  },

  initialize({ options: { options, context, providerCountry } }) {
    this.handleAssetReady = this.handleAssetReady.bind(this);
    this.handleAdError = this.handleAdError.bind(this);
    this.options = options;
    this.context = context;
    this.providerCountry = providerCountry;
    // workaround for https://github.schibsted.io/svp/web-player/issues/1339
    context.adnPlugin = this;
    this.skipAd = options.noFirstAd ?? false;
  },

  setup() {
    if (!this.ppidWrapper) {
      this.ppidWrapper = new PpidWrapper(this.player);
    }

    this.context.player = this.player;
    if (['vgtv', 'ab'].includes(getConfigProvider(this.player))) {
      this.listenTo(this.player, 'adError', this.handleAdError);
    }
    this.listenTo(this.player, 'assetReady', this.handleAssetReady);
    this.handleAssetReady();

    if (this.providerCountry === 'se') {
      // Both the classic ADN path and the VMAP path rely on this manager to
      // determine bumper/AFP state, so it must be wired up regardless of
      // which ad path ends up serving the request.
      this.context.autoplayManager?.setPlayer(this.player);
    }
  },

  get siteConfig() {
    return adsConfig[this.options.site];
  },

  handleAssetReady() {
    const nextAsset = this.getAsset();

    if (this.skipAd && this.asset && this.asset.id !== nextAsset?.id) {
      this.skipAd = false;
    }

    this.asset = nextAsset;
  },

  getPlacement(placementType) {
    const {
      deviceType,
      options: { ads = {} },
    } = this;
    if (!ads[placementType]) {
      return undefined;
    }

    return this.siteConfig?.placements?.[placementType]?.[deviceType];
  },

  hasPlacement(placementType) {
    return Boolean(this.getPlacement(placementType));
  },

  getAsset() {
    const asset = this.player.getRawAsset();
    if (
      asset &&
      asset.streamType !== ('mock' as unknown as Asset['streamType'])
    ) {
      return asset;
    }
    return undefined;
  },

  get deviceType() {
    const deviceType = getDeviceType();
    return deviceType === 'mobile' ? 'phone' : deviceType;
  },

  get adsDisabled() {
    return (
      this.config.settings?.na ||
      this.skipAd ||
      this.asset?.additional?.settings?.showAds === false
    );
  },

  get muted() {
    return Boolean(this.player.getMute() ?? this.config.mute);
  },

  get config() {
    return this.player.getRawConfig();
  },

  get prerollFormat() {
    if (this.providerCountry === 'se') {
      return this.context.autoplayManager?.getPrerollFormat() ?? 'preroll';
    }

    if (
      (getPropertyFromAssetOrCategory(this.asset, 'sponsorroll') === 'true' ||
        this.asset?.additional?.tags?.includes(
          'fce06749-ff57-406d-85e5-c6ff92ff6e29' // VG Lista
        )) &&
      this.hasPlacement('sponsorroll')
    ) {
      return 'sponsorroll';
    }

    if (this.muted && this.hasPlacement('bumper')) {
      return 'bumper';
    }

    return 'preroll';
  },

  get pcFormat() {
    const { prerollFormat, adsDisabled } = this;
    if (prerollFormat === 'bumper') {
      return adsDisabled ? 'skip' : 'bumper';
    }

    return adsDisabled ? 'adfree' : 'preroll';
  },

  get prerollSubtype() {
    const { prerollFormat } = this;

    const customType = getPropertyFromAssetOrCategory(
      this.asset,
      'forcePrerollPlacementType'
    )?.trim() as 'preroll' | 'longform' | 'sponsorroll' | undefined;

    if (customType && this.hasPlacement(customType)) {
      return customType;
    }

    if (prerollFormat === 'preroll') {
      const { duration = 0 } = this.asset || {};
      if (duration > LONGFORM_DURATION && this.hasPlacement('longform')) {
        return 'longform';
      }
    }

    return prerollFormat;
  },

  async handleAdError(err) {
    const assetId = this.player.getRawAsset()?.id;
    if (!assetId || assetId < 1) {
      return;
    }

    const tracker = this.player.getPlugin('PulseStats');
    if (!tracker) {
      return;
    }

    const params = new URLSearchParams(err.tag?.split('?')?.[1]);
    const adPlacementId = params.get('id') || params.get('inv_code');
    const adFormat =
      params.get('kw_no-sno-adformat') || params.get('kw_se-generic-adformat');

    tracker.trackMediaEvent({
      'intent': 'Attempt',
      '@type': 'Engagement',
      'position': -1,
      'duration': -1,
      'object': {
        '@id': `sdrn:${getAssetProvider(this.player)}:mediaad:0`,
        '@type': 'MediaAd',
        'duration': -1,
        'mediaAssetId': `sdrn:${getAssetProvider(this.player)}:mediaasset:${assetId}`,
        'spt:custom': {
          'spt:adErrorCodeJw': err.adErrorCode,
          'spt:adErrorMessage': err.message,
          'spt:adOffset': err.offset,
          'spt:adPlacementId': adPlacementId,
          'spt:adFormat': adFormat,
          'spt:creativeId': err.creativeId,
        },
      },
    });
  },

  async getVmap({ site, experimentMap, consentManager }) {
    const dynamicAR =
      experimentMap['ads-aspect-ratio'] === 'true' &&
      getScreenOrientation() === 'portrait';

    const cmpConsent = await getAdPermission(consentManager);

    let keywords: XandrKeywords;
    // VMAP site override - SE branch only. Pass through `ads.section` as-is
    // (no knownSections validation / defaultSection fallback) since that
    // resolution only applies to the classic ADN inv_code path.
    const resolvedSite =
      this.providerCountry === 'se'
        ? (this.options.ads?.section ?? site)
        : site;

    if (this.providerCountry === 'se') {
      // AB/SvD draw every request-level keyword from the single shared source
      // so the VMAP path matches the classic ADN path exactly.
      const seSiteConfig: AdnSiteConfig<'ab' | 'svd'> | undefined =
        this.options.site === 'svd'
          ? configSvd[this.options.site]
          : configAb[this.options.site];
      const isLoggedIn = await resolveIsLoggedIn(this.context);

      keywords = await getVmapKeywords({
        player: this.player,
        asset: this.asset!,
        deviceType: getDeviceType() as DeviceType,
        countryCode: this.providerCountry,
        supplyType: `web_${getDeviceType()}`,
        dynamicAR,
        cmpConsent,
        siteConfig: seSiteConfig!,
        ads: this.options.ads,
        isLoggedIn,
        pulseUserId: getUserId(),
      });
    } else {
      // NO (vgtv) composes the shared client + glimr stages; consent is not
      // emitted on this path (no cmp/gdpr keywords).
      keywords = prepareXandrKeywords({
        ...this.context.getClientKeywords(
          dynamicAR,
          getAssetAspectRatio(this.asset),
          getPlayerAspectRatio(this.player)
        ),
        ...(await buildConsentKeywords({
          player: this.player,
          glimrConfig: this.siteConfig?.glimr,
          cmpConsent,
          includeTcfConsent: false,
          includeAdConsent: false,
        })),
      });
    }

    const params: SvpVmapParams = {
      supplyType: `web_${getDeviceType()}`,
      site: resolvedSite,
      bumper: this.prerollFormat === 'bumper',
      client: encodeURIComponent(
        new URLSearchParams(keywords as Record<string, string>).toString()
      ),
      tcf_consent: cmpConsent === '1',
      externalUid: getUserId(),
      eids: await this.ppidWrapper.getEids(),
    };
    return Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== false && v !== '')
      .reduce((aggr, [k, v]) => ({ ...aggr, [k]: String(v) }), {});
  },
};

window.SVP.Player.Plugins.register(adnPlugin);
